// Generated from Swagger on 2025-08-27T14:46:24.221Z
import {
  useSuspenseQuery,
  useQuery,
  useMutation,
  useQueryClient,
  UseSuspenseQueryOptions,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { ApiRequestOptions, useApiClient } from "../client";
import { creationEntitiesKeys } from "../creation-entities-keys";
import {
  ApplyManagedImageEditRequestDto,
  ManagedImageResponseDto,
} from "../interfaces/media.interface";
import {
  ApplyOrganizationLogoEditRequestDto,
  OrganizationCustomerDetailsResponseDto,
  CreateOrganizationRequestDto,
  OrganizationResponseDto,
  UpdateOrganizationRequestDto,
  OrganizationCreationDetailsResponseDto,
  OrganizationsCustomerUploadLogoFormData,
  DepartmentAiCaptionSettingsResponseDto,
  UpdateDepartmentAiCaptionSettingsRequestDto,
} from "../interfaces/organizations.interface";
import { userProfileKeys } from "./user-profile.requests";

// Query keys for organizations-related queries
export const organizationsKeys = {
  all: ["organizations"] as const,
  profile: () => [...organizationsKeys.all, "profile"] as const,
  mutation: () => [...organizationsKeys.all, "mutation"] as const,
  creation_entities: () =>
    [...organizationsKeys.all, "creation_entities"] as const,
  logo_mutation: () => [...organizationsKeys.all, "logo_mutation"] as const,
  ai_caption_settings: () => [...organizationsKeys.all, "ai_caption_settings"] as const,
};

function invalidateOrganizationsQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: organizationsKeys.all });
  queryClient.invalidateQueries({ queryKey: userProfileKeys.all });
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey as string[];
      return queryKey.includes("creation-entities");
    },
  });
}

/**
 * Find organization (ALL)
 */
export function useOrganizationsCustomerFindOne(
  options?: Omit<
    UseSuspenseQueryOptions<OrganizationCustomerDetailsResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [...organizationsKeys.profile(), ...creationEntitiesKeys.list()],
    queryFn: () =>
      apiRequest<OrganizationCustomerDetailsResponseDto>(`/organizations`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useOrganizationsCustomerFindOne
 */
export function useOrganizationsCustomerFindOneQuery(
  options?: Omit<
    UseQueryOptions<OrganizationCustomerDetailsResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [...organizationsKeys.profile(), ...creationEntitiesKeys.list()],
    queryFn: () =>
      apiRequest<OrganizationCustomerDetailsResponseDto>(`/organizations`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Create organization
 */
export function useOrganizationsCustomerCreate(
  options?: Omit<UseMutationOptions<
    OrganizationResponseDto,
    Error,
    CreateOrganizationRequestDto
  >, "mutationFn">,
  requestOptions?: ApiRequestOptions,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: organizationsKeys.mutation(),
    mutationFn: (data: CreateOrganizationRequestDto) =>
      apiRequest<OrganizationResponseDto>(`/organizations`, "POST", data, undefined, requestOptions),
    ...options,
    onSuccess: (data, variables, context) => {
      invalidateOrganizationsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Update organization
 */
export function useOrganizationsCustomerUpdate(
  options?: Omit<
    UseMutationOptions<any, Error, UpdateOrganizationRequestDto>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: (data: UpdateOrganizationRequestDto) =>
      apiRequest<any>(`/organizations`, "PATCH", data),
    onSuccess: (data, variables, context) => {
      invalidateOrganizationsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Find organization creation entities
 */
export function useOrganizationsCustomerCreationEntities(
  options?: Omit<
    UseSuspenseQueryOptions<OrganizationCreationDetailsResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: creationEntitiesKeys.list(),
    queryFn: () =>
      apiRequest<OrganizationCreationDetailsResponseDto>(
        `/organizations/creation-entities`,
      ),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useOrganizationsCustomerCreationEntities
 */
export function useOrganizationsCustomerCreationEntitiesQuery(
  options?: Omit<
    UseQueryOptions<OrganizationCreationDetailsResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: creationEntitiesKeys.list(),
    queryFn: () =>
      apiRequest<OrganizationCreationDetailsResponseDto>(
        `/organizations/creation-entities`,
      ),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Upload Organization Logo - Max 5MB
 */
export function useOrganizationsCustomerUploadLogo(
  options?: UseMutationOptions<ManagedImageResponseDto, Error, OrganizationsCustomerUploadLogoFormData>,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: organizationsKeys.logo_mutation(),
    mutationFn: (data: OrganizationsCustomerUploadLogoFormData) => {
      const formData = new FormData();
      if (data.file) formData.append("file", data.file);
      return apiRequest<ManagedImageResponseDto>(`/organizations/logos`, "POST", formData);
    },
    ...options,
    onSuccess: (data, variables, context) => {
      invalidateOrganizationsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete Organization Logo
 */
export function useOrganizationsCustomerDeleteLogo(
  options?: Omit<UseMutationOptions<ManagedImageResponseDto, Error, { logoId: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ logoId }: { logoId: string }) =>
      apiRequest<ManagedImageResponseDto>(`/organizations/logos/${logoId}`, "DELETE", undefined, undefined, { throwOnError: true }),
    onSuccess: (data, variables, context) => {
      invalidateOrganizationsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useOrganizationsCustomerApplyLogoEdit(
  options?: Omit<
    UseMutationOptions<ManagedImageResponseDto, Error, { logoId: string; data: ApplyOrganizationLogoEditRequestDto }>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ logoId, data }: { logoId: string; data: ApplyManagedImageEditRequestDto }) =>
      apiRequest<ManagedImageResponseDto>(`/organizations/logos/${logoId}/edit`, "POST", data),
    onSuccess: (data, variables, context) => {
      invalidateOrganizationsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useOrganizationsCustomerRestoreOriginalLogo(
  options?: Omit<UseMutationOptions<ManagedImageResponseDto, Error, { logoId: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ logoId }: { logoId: string }) =>
      apiRequest<ManagedImageResponseDto>(`/organizations/logos/${logoId}/restore-original`, "POST"),
    onSuccess: (data, variables, context) => {
      invalidateOrganizationsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useOrganizationsCustomerPreviewBackgroundRemoval(
  options?: Omit<UseMutationOptions<Blob, Error, { file: File }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();

  return useMutation({
    ...options,
    mutationFn: ({ file }: { file: File }) => {
      const formData = new FormData();
      formData.append("file", file);

      return apiRequest<Blob>(
        `/organizations/media/preview-background-removal`,
        "POST",
        formData,
        undefined,
        { responseType: "blob", throwOnError: true }
      );
    },
  });
}

export function useOrganizationsCustomerDeleteLogoByUrl(
  options?: Omit<UseMutationOptions<ManagedImageResponseDto, Error, string>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async (logoUrl: string) =>
      apiRequest<ManagedImageResponseDto>(
        `/organizations/logos?id=${encodeURIComponent(logoUrl)}`,
        "DELETE",
        undefined,
        undefined,
        { throwOnError: true }
      ),
    onSuccess: (data, variables, context) => {
      invalidateOrganizationsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useOrganizationsDepartmentAiCaptionSettingsQuery(
  options?: Omit<
    UseQueryOptions<DepartmentAiCaptionSettingsResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: organizationsKeys.ai_caption_settings(),
    queryFn: () =>
      apiRequest<DepartmentAiCaptionSettingsResponseDto>(`/organizations/ai-caption-settings`),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    ...options,
  });
}

export function useOrganizationsUpdateDepartmentAiCaptionSettings(
  options?: Omit<
    UseMutationOptions<void, Error, UpdateDepartmentAiCaptionSettingsRequestDto>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateDepartmentAiCaptionSettingsRequestDto) =>
      apiRequest<void>(`/organizations/ai-caption-settings`, "PATCH", data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: organizationsKeys.ai_caption_settings() });
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
}
